import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import { Close, Save, Send } from '@mui/icons-material';
import { ref, getDownloadURL } from 'firebase/storage';
import { getUser } from 'components/api/user-api/UserAPI';
import { FilterAutocomplete } from 'components/helper/filter-autocomplete/FilterAutocomplete';
import { storage } from '../../../firebase-config';
import GenericAutocomplete from '../../helper/generic-autocomplete/GenericAutocomplete';
import { updateDocument } from '../../api/document-api/DocumentAPI';
import { VendorStatementDoc } from '../types';
import { VendorStatementLineItemRow } from './VendorStatementLineItemRow';
import { VendorStatementFormData, FormLineItem, AssociationOption } from './types';
import { validateForm } from './helpers';
import { PDFViewer } from './PDFViewer';

/**
 * Generates a blank line item
 * @returns
 */
const getBlankLineItem = (): FormLineItem => ({
  invoiceDate: '',
  invoiceNumber: '',
  description: '',
  poNumber: '',
  originalInvoiceAmount: '',
  amountPaid: '',
  hidden: false,
});

/**
 * Modal for editing a vendor statement job
 * @param open - Whether the modal is open
 * @param onClose - Function to call when the modal is closed
 * @param job - The job to edit
 * @returns
 */
// eslint-disable-next-line import/prefer-default-export
export const VendorStatementModal = function ({
  open,
  onClose,
  job,
  viewOnly,
  properties,
  units,
  vendors,
}: {
  open: boolean;
  onClose: () => void;
  job: VendorStatementDoc | null;
  viewOnly: boolean;
  properties: AssociationOption[];
  units: AssociationOption[];
  vendors: AssociationOption[];
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [formData, setFormData] = useState<VendorStatementFormData>({
    statementDate: '',
    property: null,
    unit: null,
    vendor: null,
    dueDate: '',
    totalAmountDue: '',
  });
  const [lineItems, setLineItems] = useState<FormLineItem[]>([]);

  const [errors, setErrors] = useState<string[]>([]);

  /**
   * Load the PDF into the modal for viewing
   */
  const loadPdf = async () => {
    if (!job) return;

    setLoadingPdf(true);
    try {
      const pdfRef = ref(storage, `vendor-statements/${job.id}.pdf`);
      const url = await getDownloadURL(pdfRef);
      setPdfUrl(url);
    } catch (error) {
      enqueueSnackbar('Error loading PDF', { variant: 'error' });
    } finally {
      setLoadingPdf(false);
    }
  };

  /**
   * Populate the form with the data from the job
   */
  const populateForm = () => {
    if (!job?.data) return;

    const relevantProperty = properties.find((property) => property.value === job.data?.propertyId);
    const relevantUnit = units.find((unit) => unit.value === job.data?.unitId);
    const relevantVendor = vendors.find(
      (vendor) => vendor.value === [job.data?.vendorId, job.data?.vendorLocationId].join('-'),
    );

    setFormData({
      statementDate: job.data.statementDate || '',
      property: relevantProperty || null,
      unit: relevantProperty ? relevantUnit || null : null,
      vendor: relevantVendor || null,
      dueDate: job.data.dueDate || '',
      totalAmountDue: job.data.totalAmountDue?.toString() || '',
    });

    setLineItems(
      job.data.lineItems?.map((item) => ({
        invoiceDate: item.invoiceDate || '',
        invoiceNumber: item.invoiceNumber || '',
        description: item.description || '',
        poNumber: item.poNumber || '',
        originalInvoiceAmount: item.originalInvoiceAmount?.toString() || '',
        amountPaid: item.amountPaid?.toString() || '',
        hidden: !!item.hidden,
      })) || [],
    );
  };

  // Load PDF when modal opens
  useEffect(() => {
    if (open && job) {
      loadPdf();
      populateForm();
      setErrors([]);
    }
  }, [open, job]);

  /**
   * Update a field in the form data
   * @param field - The field to update
   * @param value - The value to update the field to
   */
  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Update the data for the vendor statement job - if final, mark the job as completed.
   * @param config - The configuration for the update
   * @returns
   */
  const updateData = async (config: { final: boolean } = { final: false }) => {
    if (!job) return;

    const [vendorId, vendorLocationId] = formData.vendor?.value?.split('-') || [];
    // Prepare the data to save
    const updatedData: VendorStatementDoc['data'] = {
      statementDate: formData.statementDate || null,
      dueDate: formData.dueDate || null,
      propertyId: formData.property?.value || null,
      unitId: formData.unit?.value || null,
      vendorId: vendorId || null,
      vendorLocationId: vendorLocationId || null,
      totalAmountDue: formData.totalAmountDue ? parseFloat(formData.totalAmountDue) : null,
      lineItems: lineItems.map((item) => ({
        invoiceDate: item.invoiceDate || null,
        invoiceNumber: item.invoiceNumber || null,
        description: item.description || null,
        poNumber: item.poNumber || null,
        originalInvoiceAmount: item.originalInvoiceAmount ? parseFloat(item.originalInvoiceAmount) : null,
        amountPaid: item.amountPaid ? parseFloat(item.amountPaid) : null,
        hidden: item.hidden,
      })),
    };

    const updatedDoc: Partial<VendorStatementDoc> = { data: updatedData };
    if (config.final) {
      updatedDoc.status = 'job-completed';
      updatedDoc.completedAt = new Date().toISOString();
      updatedDoc.completedBy = getUser()?.uid || null;
    }

    // Update the document
    await updateDocument('vendor-statements', job.id, updatedDoc);
  };

  /**
   * Save a draft of the vendor statement job
   */
  const handleSaveDraft = async () => {
    if (!job) return;

    try {
      await updateData();
      enqueueSnackbar('Draft saved successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to save draft. Please try again.', { variant: 'error' });
    }
  };

  /**
   * Submit the vendor statement job (basically validate the form and mark the job as completed)
   */
  const handleSubmit = async () => {
    const formErrors = validateForm(formData, lineItems);

    if (formErrors.length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      await updateData({ final: true });
      enqueueSnackbar('Vendor statement submitted successfully!', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Failed to submit vendor statement. Please try again.', { variant: 'error' });
    }
  };

  /**
   * Hide or unhide a line item on a completed vendor statement. Hidden line items are
   * excluded from the BigQuery sync. Saved immediately since completed jobs can't be edited otherwise.
   * @param index - The index of the line item to toggle
   */
  const toggleLineItemHidden = async (index: number) => {
    if (!job?.data) return;

    const hidden = !lineItems[index].hidden;
    // build from the saved line items so only the hidden flags change (the job prop isn't refreshed after a write)
    const updatedLineItems = job.data.lineItems.map((item, i) => ({
      ...item,
      hidden: i === index ? hidden : !!lineItems[i]?.hidden,
    }));

    try {
      await updateDocument('vendor-statements', job.id, { data: { ...job.data, lineItems: updatedLineItems } });
      setLineItems((prev) => prev.map((row, i) => (i === index ? { ...row, hidden } : row)));
      enqueueSnackbar(
        hidden
          ? 'Line item hidden. It will be removed from BigQuery on the next hourly sync.'
          : 'Line item unhidden. It will be added back to BigQuery on the next hourly sync.',
        { variant: 'success' },
      );
    } catch (error) {
      enqueueSnackbar('Failed to update line item. Please try again.', { variant: 'error' });
    }
  };

  const hiddenCount = lineItems.filter((item) => item.hidden).length;

  if (!job) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95vw',
          height: '95vh',
          maxWidth: 'none',
          maxHeight: 'none',
        },
      }}
    >
      <DialogContent sx={{
        p: 0, height: '100%', display: 'flex', flexDirection: 'column',
      }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#4682B4',
            color: 'white',
          }}
        >
          <Typography variant="h6" component="h2">
            Edit Vendor Statement
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>

        {/* Main Content */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Side - Basic Information Only */}
          <Box
            sx={{
              width: '20%',
              p: 3,
              overflow: 'auto',
              borderRight: 1,
              borderColor: 'divider',
            }}
          >

            {/* Basic Information */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#70A1B4' }}>
                Basic Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="* Statement Date"
                  type="date"
                  value={formData.statementDate}
                  onChange={(e) => handleFormChange('statementDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={viewOnly}
                />
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleFormChange('dueDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={viewOnly}
                />
                <FilterAutocomplete
                  label="Property"
                  selected={formData.property}
                  path="engine/propertyUnit/associations"
                  filter={(item) => item.dependentOf === null}
                  onChange={(_, value) => {
                    setFormData((prev) => ({
                      ...prev,
                      property: value,
                      unit: null, // Clear unit when property changes
                    }));
                  }}
                  noOptionsText="Loading please wait..."
                  disabled={viewOnly}
                />
                <FilterAutocomplete
                  label="Unit"
                  selected={formData.unit}
                  path="engine/propertyUnit/associations"
                  filter={(item) => item.dependentOf !== null && item.dependentOf === formData.property?.value}
                  onChange={(_, value) => {
                    setFormData((prev) => ({
                      ...prev,
                      unit: value,
                    }));
                  }}
                  disabled={!formData.property || viewOnly}
                  noOptionsText={formData.property
                    ? 'No units found for selected property'
                    : 'Please select a property first'}
                />
                <GenericAutocomplete
                  keyName="label"
                  label="* Vendor"
                  selected={formData.vendor}
                  sortKey="label"
                  path="engine/payee/associations"
                  onChange={(_, value) => {
                    setFormData((prev) => ({
                      ...prev,
                      vendor: value,
                    }));
                  }}
                  disabled={viewOnly}
                />
                <TextField
                  fullWidth
                  label="* Total Amount Due"
                  type="number"
                  value={formData.totalAmountDue}
                  onChange={(e) => handleFormChange('totalAmountDue', e.target.value)}
                  inputProps={{ step: '0.01' }}
                  disabled={viewOnly}
                />
              </Box>
            </Paper>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Show validation errors, if any */}
              {errors.length > 0 && (<Typography variant="body2" color="error">Errors:</Typography>)}
              {errors.map((error) => (
                <Typography variant="body2" color="error" key={error}>
                  -
                  {' '}
                  {error}
                </Typography>
              ))}
            </div>
            {!viewOnly && (
            <Box sx={{
              mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2,
            }}
            >
              {/* Save draft button */}
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={handleSaveDraft}
                sx={{
                  backgroundColor: 'white',
                  borderColor: '#4682B4',
                  color: '#4682B4',
                  '&:hover': {
                    backgroundColor: '#E0FFFF',
                    borderColor: '#70A1B4',
                  },
                }}
              >
                Save Draft
              </Button>
              {/* Submit button */}
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleSubmit}
                sx={{
                  backgroundColor: '#4682B4',
                  '&:hover': {
                    backgroundColor: '#70A1B4',
                  },
                }}
              >
                Submit
              </Button>
            </Box>
            )}
          </Box>

          {/* Right Side - PDF Viewer and Line Items */}
          <Box
            sx={{
              width: '80%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#E0FFFF',
            }}
          >

            {/* PDF Viewer Section */}
            <PDFViewer
              loadingPdf={loadingPdf}
              pdfUrl={pdfUrl}
            />

            {/* Line Items Section */}
            <Box sx={{
              height: '34%', borderTop: 1, borderColor: 'divider', backgroundColor: 'white',
            }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
                >
                  <Typography variant="h6" sx={{ color: '#4682B4' }}>
                    Line Items (
                    {lineItems.length}
                    {hiddenCount > 0 && `, ${hiddenCount} hidden`}
                    )
                  </Typography>
                  {/* Add line item button */}
                  {!viewOnly && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setLineItems((prev) => [...prev, getBlankLineItem()]);
                    }}
                    sx={{ color: '#4682B4', borderColor: '#4682B4' }}
                  >
                    Add Line Item
                  </Button>
                  )}
                </Box>
              </Box>
              {/* List of line items */}
              <Box sx={{ p: 2, height: 'calc(100% - 80px)', overflow: 'auto' }}>
                {lineItems.map((item, index) => (
                  <VendorStatementLineItemRow
                    item={item}
                    index={index}
                    handleLineItemChange={(idx: number, field: string, value: string) => {
                      setLineItems((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
                    }}
                    removeLineItem={(idx: number) => {
                      setLineItems((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    toggleHidden={toggleLineItemHidden}
                    viewOnly={viewOnly}
                  />
                ))}

                {lineItems.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No line items. Click
                    {' '}
                    <b>Add Line Item</b>
                    {' '}
                    to get started.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
